import React from "react";
import { Link as RouterLink } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Box, Container, Typography, Link, Stack } from "@mui/material";
import Logo from "./Logo";
import { THEMES } from "../content/themes";
import { Lang } from "../content/mutualiteiten";

export default function Footer() {
  const { t, i18n } = useTranslation();
  const lang = ((i18n.language || "nl").slice(0, 2) as Lang);
  const year = new Date().getFullYear();

  const col = (title: string, links: { label: string; to: string }[]) => (
    <Box sx={{ minWidth: 150 }}>
      <Typography sx={{ fontWeight: 600, color: "text.primary", mb: 1.5, fontSize: ".95rem" }}>{title}</Typography>
      <Stack spacing={1}>
        {links.map((l) => (
          <Link key={l.label} component={RouterLink} to={l.to} sx={{ color: "text.secondary", fontSize: ".9rem", "&:hover": { color: "primary.main" } }}>
            {l.label}
          </Link>
        ))}
      </Stack>
    </Box>
  );

  return (
    <Box component="footer" sx={{ mt: "auto", bgcolor: "var(--surface-alt)", borderTop: "1px solid var(--border)" }}>
      <Container maxWidth="lg" sx={{ py: { xs: 6, sm: 8 } }}>
        <Box sx={{ display: "flex", flexWrap: "wrap", gap: { xs: 4, sm: 6 }, justifyContent: "space-between" }}>
          <Box sx={{ maxWidth: 280 }}>
            <Box sx={{ mb: 1.5 }}><Logo height={40} mono="#8A7B6B" /></Box>
            <Typography sx={{ color: "text.secondary", fontSize: ".9rem", m: 0 }}>{t("footer.tagline")}</Typography>
          </Box>
          {col(t("footer.product"), [
            { label: t("footer.howItWorks"), to: "/hoe-het-werkt" },
            { label: t("footer.howWeMatch"), to: "/hoe-matchen-wij" },
            { label: t("footer.pricing"), to: "/tarieven" },
            { label: t("footer.reimbursement"), to: "/terugbetaling" },
            { label: t("footer.quality"), to: "/kwaliteit-en-erkenning" },
            { label: t("footer.faq"), to: "/veelgestelde-vragen" },
          ])}
          {col(t("footer.selfHelpCol"), [
            { label: t("footer.selfHelp"), to: "/zelfhulp" },
            ...THEMES.map((th) => ({ label: th.nav[lang] || th.nav.nl, to: `/thema/${th.slug}` })),
          ])}
          {col(t("footer.company"), [
            { label: t("footer.about"), to: "/over-ons" },
            { label: t("footer.contact"), to: "/contact" },
            { label: t("footer.forOrganisations"), to: "/voor-werkgevers" },
            { label: t("footer.barometer"), to: "/welzijnsbarometer" },
            { label: t("footer.forPsychologists"), to: "/voor-psychologen" },
          ])}
          {col(t("footer.legal"), [
            { label: t("footer.terms"), to: "/voorwaarden" },
            { label: t("footer.privacy"), to: "/privacy" },
            { label: t("footer.privacyStandard"), to: "/privacystandaard" },
            { label: t("footer.cookies"), to: "/cookies" },
            { label: t("footer.subProcessors"), to: "/sub-verwerkers" },
            { label: t("footer.myData"), to: "/mijn-gegevens" },
          ])}
          <Box sx={{ minWidth: 200 }}>
            <Typography sx={{ fontWeight: 600, color: "text.primary", mb: 1, fontSize: ".95rem" }}>{t("footer.crisisTitle")}</Typography>
            <Typography sx={{ color: "text.secondary", fontSize: ".9rem", m: 0 }}>{t("footer.crisisLine")}</Typography>
          </Box>
        </Box>

        {/* Colofon — identification required by art. III.74 + XII.6 WER. */}
        <Box sx={{ mt: 5, pt: 3, borderTop: "1px solid var(--border)" }}>
          <Typography sx={{ fontWeight: 600, color: "text.primary", fontSize: ".9rem", mb: 0.75 }}>{t("footer.colofonTitle")}</Typography>
          <Typography sx={{ color: "text.secondary", fontSize: ".82rem", m: 0 }}>{t("footer.colofonCompany")}</Typography>
          <Typography sx={{ color: "text.secondary", fontSize: ".82rem", m: 0 }}>{t("footer.colofonRegistry")}</Typography>
          <Typography sx={{ color: "text.secondary", fontSize: ".82rem", m: 0 }}>{t("footer.colofonAddress")}</Typography>
          <Typography sx={{ color: "text.secondary", fontSize: ".82rem", m: 0 }}>
            <Link href="mailto:info@carefunded.be" sx={{ color: "inherit" }}>info@carefunded.be</Link>
            {" · "}
            <Link href="mailto:support@carefunded.be" sx={{ color: "inherit" }}>support@carefunded.be</Link>
            {" · "}
            <Link component={RouterLink} to="/voorwaarden" sx={{ color: "inherit" }}>{t("footer.colofonTerms")}</Link>
          </Typography>
        </Box>

        <Box sx={{ mt: 3, pt: 3, borderTop: "1px solid var(--border)" }}>
          <Typography sx={{ color: "text.secondary", fontSize: ".82rem", m: 0 }}>{t("footer.copyright", { year })}</Typography>
          <Typography sx={{ color: "text.secondary", fontSize: ".82rem", mt: 0.5, mb: 0 }}>{t("footer.disclaimer")}</Typography>
        </Box>
      </Container>
    </Box>
  );
}
